import { InteractionStepType, StepParamsForType } from '@scale/shared/types/chat';
import { usePerStepResponseLinter } from './usePerStepResponseLinter/hook';
import { useChatStoreRef, useStepParams } from '../../Chat/hooks/ChatStore.hooks';
import { useCallback, useContext } from 'react';
import { useLocalStorageBackupStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useLocalStorageBackupStore';
import { useChatStepTimerLogStepSubmission } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatStepTimer';
import { toast } from 'react-toastify';
import { usePerStepContributorFeedback } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/hooks/usePerStepContributorFeedback';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { isUndefined } from 'lodash';
import { useContributorFeedbackImperativeApi } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ContributorFeedback/stores/useContributorFeedbackStore';
import { useWorkerCommentsStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';

type StepSubmitHandlerProps = {
  responseIndex: number;
};

type StepSubmitHandlerReturnType = {
  onSubmit: (
    newResponse: any,
    index: number,
    stepParams: StepParamsForType<InteractionStepType>,
    shouldResetHistory?: boolean,
    shouldSkipLinting?: boolean,
    shouldHackilyUpdateNextMultiTurnContinueStep?: boolean,
  ) => Promise<boolean>;
} & ReturnType<typeof usePerStepResponseLinter>;

// TODO: @Lajos refactor to use references instead
export const useStepSubmitHandler = <U extends InteractionStepType = InteractionStepType>(
  props: StepSubmitHandlerProps,
): StepSubmitHandlerReturnType => {
  const { assignmentId, disableLinter, enableUnifiedFeedback } = useContext(ChatTaskContext);
  const stepParams = useStepParams<U>(props.responseIndex);
  const chatStoreRef = useChatStoreRef();
  const stepResponseLinterHook = usePerStepResponseLinter();
  const contributorFeedbackHook = usePerStepContributorFeedback({
    stepParams,
    // TODO: Generally, the cases where we want to disable linters (i.e preview pages, audits, etc), we also want to disable other
    // automated feedback. But there could be future cases where this isn't true.
    disableFeedback: disableLinter,
  });
  const resetWorkerCommentsFromResponseIndex = useWorkerCommentsStore(
    state => state.resetWorkerCommentsFromResponseIndex,
  );
  const contributorFeedbackStoreInstance = useContributorFeedbackImperativeApi();
  const { lintStepResponse } = stepResponseLinterHook;
  const { fetchContributorFeedbackOnStepSubmit } = contributorFeedbackHook;

  const bulkClearBackupsForAssignment = useLocalStorageBackupStore(
    state => state.bulkClearBackupsForAssignment,
  );
  const logStepSubmission = useChatStepTimerLogStepSubmission();

  const fetchFeedbackAndNotify = useCallback(
    async (
      fetchPromise: Promise<boolean | undefined>,
      errorMessage: string,
    ): Promise<boolean | undefined> => {
      const currentState = chatStoreRef.current.getState();
      const setIsValidating = currentState.setIsPerStepLinterValidating;
      const isValid = await fetchPromise;
      setIsValidating(false);

      if (!isValid) {
        toast.error(errorMessage);
        return false;
      }

      return !!isValid;
    },
    [chatStoreRef],
  );

  const onSubmit = useCallback(
    // eslint-disable-next-line complexity
    async (
      newResponse: any,
      index: number,
      stepParams: StepParamsForType<InteractionStepType>,
      shouldResetHistory?: boolean,
      shouldSkipLinting?: boolean,
      shouldHackilyUpdateNextMultiTurnContinueStep?: boolean,
    ) => {
      if (isUndefined(index)) {
        return false;
      }

      const currentState = chatStoreRef.current.getState();
      const setResponseForIndex = currentState.setResponseForIndex;

      const isValid = shouldSkipLinting
        ? true
        : enableUnifiedFeedback
        ? await fetchFeedbackAndNotify(
            fetchContributorFeedbackOnStepSubmit(newResponse, index),
            'Please resolve the potential issues before submitting this step.',
          )
        : await fetchFeedbackAndNotify(
            lintStepResponse(stepParams, newResponse, index),
            'Please resolve the linting errors before submitting this step.',
          );

      if (!isValid) {
        return false;
      }

      const allStepParams = currentState.stepParams;
      if (shouldResetHistory && allStepParams) {
        if (enableUnifiedFeedback) {
          contributorFeedbackStoreInstance
            .getState()
            .resetAutomatedContributorFeedbackFromResponseIndex(allStepParams, index);
        }
        resetWorkerCommentsFromResponseIndex(allStepParams, index);
      }

      if (shouldResetHistory && assignmentId) {
        bulkClearBackupsForAssignment(assignmentId, index);
      }

      setResponseForIndex(newResponse, index, shouldResetHistory);

      // Only designed for project: 683e729ecaf6d16374e6fc5b
      if (
        shouldHackilyUpdateNextMultiTurnContinueStep &&
        stepParams.type === InteractionStepType.ModelResponseSelector &&
        !shouldResetHistory
      ) {
        // Find the next multi-turn continue step and update it
        const nextResponseEditorStep = currentState.responses.find(
          step =>
            step.type === InteractionStepType.ModelResponseEditor &&
            step.index > index &&
            step.contextType !== 'Skipped',
        );
        const nextMultiTurnContinueStep = currentState.responses.find(
          step =>
            step.type === InteractionStepType.MultiTurnContinue &&
            step.index > index &&
            step.contextType !== 'Skipped',
        );
        if (nextMultiTurnContinueStep) {
          const selectedCandidate =
            newResponse.context.candidates[newResponse.context.selectedIndex];
          if (
            nextResponseEditorStep &&
            nextResponseEditorStep.index < nextMultiTurnContinueStep.index
          ) {
            nextResponseEditorStep.context.candidate = selectedCandidate;
            nextResponseEditorStep.output = selectedCandidate.message.content;
            if (selectedCandidate.message?.messageMetadata?.tool_calls) {
              nextResponseEditorStep.output +=
                '```json\n' +
                JSON.stringify(selectedCandidate.message.messageMetadata.tool_calls, null, 2) +
                '\n```';
              nextResponseEditorStep.context.originalMetadata =
                selectedCandidate.message.messageMetadata;
            }
            setResponseForIndex(
              nextResponseEditorStep,
              nextResponseEditorStep.index,
              shouldResetHistory,
            );
          }
          nextMultiTurnContinueStep.context.message = selectedCandidate.message.content;
          if (nextMultiTurnContinueStep.context.modelResponseMetadata) {
            nextMultiTurnContinueStep.context.modelResponseMetadata = {
              ...selectedCandidate.message.messageMetadata,
              conversationMetadata:
                nextMultiTurnContinueStep.context.modelResponseMetadata?.conversationMetadata,
            };
          }
          // assistant with tool calls
          if (selectedCandidate.message?.messageMetadata?.tool_calls) {
            nextMultiTurnContinueStep.context.message +=
              '```json\n' +
              JSON.stringify(selectedCandidate.message.messageMetadata.tool_calls, null, 2) +
              '\n```';
            if (
              selectedCandidate.message?.messageMetadata?.tool_calls &&
              Array.isArray(selectedCandidate.message.messageMetadata.tool_calls) &&
              nextMultiTurnContinueStep.context.modelResponseMetadata
            ) {
              nextMultiTurnContinueStep.context.modelResponseMetadata.content =
                selectedCandidate.message.content;
            }
          }
          // tool call
          if (selectedCandidate.message?.messageMetadata?.tool_call_id) {
            nextMultiTurnContinueStep.context.message =
              'Tool Call Id: ' +
              selectedCandidate.message.messageMetadata.tool_call_id +
              ': ' +
              '\n\n```json\n' +
              JSON.stringify(selectedCandidate.message.messageMetadata.tool_call_id, null, 2) +
              '\n```';
          }

          setResponseForIndex(
            nextMultiTurnContinueStep,
            nextMultiTurnContinueStep.index,
            shouldResetHistory,
          );
        }
      }

      try {
        logStepSubmission(stepParams.id);
      } catch {
        // ignored
      }

      return true;
    },
    [
      chatStoreRef,
      assignmentId,
      enableUnifiedFeedback,
      contributorFeedbackStoreInstance,
      fetchContributorFeedbackOnStepSubmit,
      lintStepResponse,
      bulkClearBackupsForAssignment,
      logStepSubmission,
      fetchFeedbackAndNotify,
      resetWorkerCommentsFromResponseIndex,
    ],
  );

  return { onSubmit, ...stepResponseLinterHook };
};
